import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Requests a right-sized, compressed, next-gen (webp/avif) render from Unsplash
// instead of shipping the ~1600px q=80 original to every viewport.
const optimizedUrl = (url, width) => {
  try {
    const u = new URL(url);
    u.searchParams.set('w', width);
    u.searchParams.set('q', '60');
    u.searchParams.set('auto', 'format,compress');
    u.searchParams.set('fit', 'crop');
    return u.toString();
  } catch {
    return url;
  }
};

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);

  const services = [
    {
      title: "Digital Transformation Consulting",
      image: "https://images.unsplash.com/photo-1634128510033-a548c3456cad?q=80&w=1659&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "We help businesses navigate digital transformation with strategic consulting services."
    },
    {
      title: "AI Enabled Innovation",
      image: "https://images.unsplash.com/photo-1642893013812-fae55caf38e9?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "Leverage AI to drive innovation and gain competitive advantage in your industry."
    },
    {
      title: "Modern Enterprise Architecture",
      image: "https://images.unsplash.com/photo-1719702702684-8d26ab9726fb?q=80&w=1528&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "Build scalable and future-proof enterprise architectures for your organization."
    },
    {
      title: "Scalable Multi-Tenant Applications",
      image: "https://images.unsplash.com/photo-1736721143022-c0eeb256f062?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3DD",
      description: "Develop applications that scale effortlessly with your growing customer base."
    },
    {
      title: "Web & Mobile Solutions",
      image: "https://images.unsplash.com/photo-1624571409108-e9a41746af53?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "Beautiful and functional web and mobile applications tailored to your needs."
    },
    {
      title: "Stunning User Experience",
      image: "https://images.unsplash.com/photo-1586780807983-950860a50ece?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "Create memorable user experiences that keep customers coming back."
    },
    {
      title: "Robust Infrastructure & Cloud",
      image: "https://images.unsplash.com/photo-1723407780066-4a979d4e499a?q=80&w=1615&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      description: "Reliable and scalable infrastructure solutions for your business needs."
    }
  ];

  // Gradient colors for animation
const gradients = [
  "from-red-300 to-orange-500",       // Cool blue gradient
  "from-yellow-300 to-yellow-300",    // Fresh green gradient
  "from-yellow-300 to-red-400",   // Royal purple gradient
  "from-green-300 to-green-300",    // Warm orange gradient
  "from-sky-200 to-green-500",       // Romantic pink gradient
  "from-yellow-300 to-rose-300",        // Bright cyan gradient
 "from-red-400 to-pink-500"    // Deep violet gradient
];

  // Auto-rotate services
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % services.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [services.length]);

  const slideToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % services.length);
  };

  const slideToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-md p-6 bg-white rounded-xl shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center justify-center">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0 transition-opacity duration-1000">
        <img
          src={optimizedUrl(services[currentIndex].image, 1600)}
          srcSet={`${optimizedUrl(services[currentIndex].image, 640)} 640w, ${optimizedUrl(services[currentIndex].image, 1024)} 1024w, ${optimizedUrl(services[currentIndex].image, 1600)} 1600w`}
          sizes="100vw"
          width="1600"
          height="900"
          alt={services[currentIndex].title}
          className="w-full h-full object-cover transition-opacity duration-1000"
          // This is the LCP element on first paint, so it must never be lazy-loaded.
          loading="eager"
          fetchPriority="high"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/1920x1080/1a202c/ffffff?text=Digital+Services';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-4 py-20 sm:py-24">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
          Driving Digital Growth <span className="block text-2xl sm:text-3xl font-normal mt-2">Leveraging</span>
        </h1>
        
        <div className="relative inline-flex items-center group mb-6 sm:mb-8">
          {/* Left Arrow */}
          <button
            onClick={slideToPrev}
            className="absolute -left-26 sm:-left-30 top-1/3 transform -translate-y-1/2 z-20 transition-all duration-300 hover:scale-110 bg-white/20 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg hover:shadow-xl hover:bg-white/30"
            aria-label="Previous service"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>

          {/* Changing Text */}
          <div className="transition-all duration-500">
            <span className={`text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r ${gradients[currentIndex]} bg-clip-text text-transparent`}>
              {services[currentIndex].title}
            </span>
            <p className="mt-4 text-lg text-gray-200 max-w-2xl mx-auto">
              {services[currentIndex].description}
            </p>
          </div>

          {/* Right Arrow */}
          <button
            onClick={slideToNext}
            className="absolute -right-26 sm:-right-30 top-1/3 transform -translate-y-1/2 z-20 transition-all duration-300 hover:scale-110 bg-white/20 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg hover:shadow-xl hover:bg-white/30"
            aria-label="Next service"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <button
            onClick={() => document.getElementById('ContactUs')?.scrollIntoView({ behavior: 'smooth' })}
            className="relative overflow-hidden w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group hover:from-blue-700 hover:to-blue-600"
          >
            <span className="relative z-10">Get Started</span>
          </button>
          <button
            onClick={() => document.getElementById('AboutSection')?.scrollIntoView({ behavior: 'smooth' })}
            className="relative overflow-hidden w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group hover:bg-white/20"
          >
            <span className="relative z-10">Explore More</span>
          </button>
        </div>

        {/* Trust indicators */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-300 text-sm">
          <div className="flex items-center gap-8">
            <div className="flex -space-x-3 relative">
              {[
                "https://randomuser.me/api/portraits/women/44.jpg",
                "https://randomuser.me/api/portraits/men/32.jpg",
                "https://randomuser.me/api/portraits/women/68.jpg",
                "https://randomuser.me/api/portraits/men/75.jpg"
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Client testimonial avatar ${i + 1}`}
                  width="40"
                  height="40"
                  className="w-10 h-10 rounded-full border-2 border-gray-800 object-cover hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://i.pravatar.cc/150?img=${i + 1}`;
                  }}
                />
              ))}
              <div className="absolute -right-6 top-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 border-2 border-gray-800 text-white font-medium text-sm">
                +30
              </div>
            </div>
            <div>
              <span className="text-white font-medium">Trusted by 30+ Global Clients </span>
            </div>
          </div>
          {/* <div className="hidden sm:block w-px h-6 bg-gray-500"></div> */}
          {/* <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-white">4.9 Rating (50+ Reviews)</span>
          </div> */}
        </div>
      </div>

      {/* Floating dots animation */}
      <div className="absolute bottom-10 left-0 right-0 hidden md:flex justify-center space-x-4 z-10">
        {services.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${i === currentIndex ? 'bg-blue-400 w-6' : 'bg-white/30'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;